// The brands one deployment answers as: the hostname picks the name, the icons and the links; everything else (code,
// database, accounts) is shared. A new brand is one entry here plus its icons in apps/web/public/brands/<id>/.
// Pure, used by the Worker and the web app alike.

export type BrandId = "filmsquare" | "dreamsquare";

export interface BrandIcons {
  ico: string;
  svg: string;
  apple: string;
}

export interface Brand {
  id: BrandId;
  name: string;
  /** Hostnames that show this brand; `*.localhost` is for trying a brand on a dev server. */
  hosts: readonly string[];
  /** Origin of the studio for links made outside a request (mail). Null: PUBLIC_BASE_URL. */
  appOrigin: string | null;
  /** Sender of its mail. Null: MAIL_FROM. */
  mailFrom: string | null;
  /** The look of its sign-in mail. */
  mail: { background: string; ink: string; muted: string; headingFont: string };
  icons: BrandIcons;
  /** The mark as a single-colour image, used as a mask. Null: the nine department squares. */
  mark: string | null;
  /** The two lines on the sign-in screen. */
  promise: readonly [string, string];
  /** Accounts are made through an invitation: the sign-in screen offers no sign-up of its own. */
  inviteOnly: boolean;
  /** PostHog runs on this brand's host. Without it there is nothing to ask consent for: no banner, no cookie settings. */
  analytics: boolean;
  /** The brand's own sign-in screen. Null: the studio's. */
  login: BrandLogin | null;
}

export interface BrandLogin {
  /** The wordmark, in place of the mark and the name. */
  logo: string;
  /** The picture behind the form, and the poster of its video. */
  art: string;
  /** The silent clip behind the form, played once and held on its last frame: smallest first. Served with Range by the Worker (wrangler.toml), or Safari skips it. */
  video: readonly { src: string; type: string }[];
  /** The stylesheet with its typefaces. */
  fonts: string;
  /** The line at the bottom. */
  foot: string;
}

export const DEFAULT_BRAND: Brand = {
  id: "filmsquare",
  name: "Filmsquare",
  hosts: [],
  appOrigin: null,
  mailFrom: null,
  mail: {
    background: "#0b0b0c",
    ink: "#ffffff",
    muted: "#9a9aa0",
    headingFont: "-apple-system,'Segoe UI',Helvetica,Arial,sans-serif",
  },
  icons: { ico: "/favicon.ico", svg: "/favicon.svg", apple: "/apple-touch-icon.png" },
  mark: null,
  promise: ["Make a real film.", "Without a real film budget."],
  inviteOnly: false,
  analytics: true,
  login: null,
};

const DREAMSQUARE: Brand = {
  id: "dreamsquare",
  name: "Dreamsquare",
  hosts: ["app.dreamsquare.studio", "dreamsquare.localhost"],
  appOrigin: "https://app.dreamsquare.studio",
  // dreamsquare.com is verified at Resend; a dreamsquare.studio address needs that domain verified first
  mailFrom: "Dreamsquare <noreply@dreamsquare.com>",
  mail: {
    background: "#06040f",
    ink: "#fffcf0",
    muted: "#a3a096",
    headingFont: "Georgia,'Times New Roman',serif",
  },
  icons: {
    ico: "/brands/dreamsquare/favicon-32x32.png",
    svg: "/brands/dreamsquare/favicon.svg",
    apple: "/brands/dreamsquare/apple-touch-icon.png",
  },
  mark: "/brands/dreamsquare/mark.png",
  promise: ["One studio,", "first page to final frame."],
  inviteOnly: true,
  analytics: false,
  login: {
    logo: "/brands/dreamsquare/logo.png",
    art: "/brands/dreamsquare/login.webp",
    video: [
      { src: "/brands/dreamsquare/login.av1.mp4", type: 'video/mp4; codecs="av01.0.08M.08"' },
      { src: "/brands/dreamsquare/login.mp4", type: "video/mp4" },
    ],
    fonts:
      "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600&family=IBM+Plex+Mono&family=Newsreader:ital,opsz,wght@0,6..72,400;1,6..72,400&display=swap",
    foot: "Dreamsquare Studio · AI Film Studio",
  },
};

const BRANDS: readonly Brand[] = [DEFAULT_BRAND, DREAMSQUARE];

/** The brand of a hostname. Any host we do not know (a self-hosted studio, localhost) is the default brand. */
export function brandForHost(hostname: string): Brand {
  const host = hostname.toLowerCase();
  return BRANDS.find((b) => b.hosts.includes(host)) ?? DEFAULT_BRAND;
}

/** The brand stored with a record; an unknown id (a brand since removed) is the default brand. */
export function brandById(id: string | null | undefined): Brand {
  return BRANDS.find((b) => b.id === id) ?? DEFAULT_BRAND;
}

/**
 * Where the browser goes back to after leaving the studio (a payment, an outside sign-in). On the default brand the
 * stable PUBLIC_BASE_URL, on another brand the origin it came from: a session lives per origin, so going back to the
 * default host would sign the person out.
 */
export function appOriginOf(requestUrl: string, publicBaseUrl?: string): string {
  const u = new URL(requestUrl);
  if (brandForHost(u.hostname).id !== DEFAULT_BRAND.id) return u.origin;
  let end = publicBaseUrl?.length ?? 0;
  while (end > 0 && publicBaseUrl?.[end - 1] === "/") end--;
  return publicBaseUrl?.slice(0, end) || u.origin;
}

/** index.html as this brand: the title and icons are in the first response, so nothing flashes and link previews agree. */
export function brandedIndexHtml(html: string, brand: Brand): string {
  return html
    .replace(/<title>[^<]*<\/title>/, `<title>${brand.name}</title>`)
    .replaceAll(DEFAULT_BRAND.icons.ico, brand.icons.ico)
    .replaceAll(DEFAULT_BRAND.icons.svg, brand.icons.svg)
    .replaceAll(DEFAULT_BRAND.icons.apple, brand.icons.apple);
}
